import { z } from 'zod'

export const searchContactsSchema = z.object({
  term: z.string().min(1).describe('Name, phone number, or email to search for'),
  page: z.number().int().min(1).default(1),
  per_page: z.number().int().min(1).max(50).default(10),
})

export const getContactSchema = z.object({
  id: z.number().int().describe('Contact id'),
})

export const openContactSchema = z.object({
  // Coerce: the id may arrive as a string from prior tool output; accept both.
  contact_id: z.coerce
    .number()
    .int()
    .describe('Contact id to open (from search_contacts / search_contacts_list)'),
})

export const searchContactsListSchema = z.object({
  term: z
    .string()
    .default('')
    .describe('Search text to apply to the on-screen contacts list; empty clears the search'),
})

export const createContactSchema = z.object({
  number: z.string().min(1).describe('E.164 phone number, e.g. +15551234567'),
  email: z.string().email().nullish(),
  first_name: z.string().nullish(),
  last_name: z.string().nullish(),
})

export const updateContactSchema = z.object({
  id: z.number().int().describe('Contact id to update'),
  number: z.string().optional(),
  email: z.string().email().optional(),
  first_name: z.string().optional(),
  last_name: z.string().optional(),
})

export const tagContactSchema = z.object({
  contact_id: z.number().int().describe('Contact id to tag'),
  tags: z
    .array(z.object({ id: z.number().int(), label: z.string() }))
    .min(1)
    .describe('Tags to apply; each needs an existing tag id and label'),
})

export const listTagsSchema = z.object({})

export const createTagSchema = z.object({
  label: z.string().min(1).describe('The label for the new tag'),
})

export const listInboxesSchema = z.object({})

export const sendMessageSchema = z.object({
  contact_id: z.number().int().describe('Contact id to message'),
  message: z.string().min(1).describe('SMS body to send'),
  team_id: z.number().int().describe('Sending inbox/team id (from list_inboxes)'),
})

export const listUnreadConversationsSchema = z.object({})

export const replyToConversationSchema = z.object({
  conversation_id: z
    .number()
    .int()
    .describe('Conversation id to reply to (from list_unread_conversations)'),
  message: z.string().min(1).describe('Reply text to send'),
})

export const listBroadcastNumbersSchema = z.object({})

export const openBroadcastCreateSchema = z.object({})

export const setBroadcastDetailsSchema = z.object({
  name: z.string().min(1).optional().describe('Broadcast name'),
  message: z.string().min(1).optional().describe('SMS body to send'),
})

export const setBroadcastAudienceSchema = z.object({
  tag_ids: z.array(z.number().int()).optional().describe('Audience: tag ids from list_tags'),
  contact_ids: z.array(z.number().int()).optional().describe('Explicit recipient contact ids'),
})

export const setBroadcastSenderSchema = z.object({
  // Coerce: ask_user_choice returns the chosen value as a string ("123"), so the agent
  // may pass a string number_id; coercion accepts it without a validation stall.
  number_id: z.coerce
    .number()
    .int()
    .describe('Sending number id (number_id from list_broadcast_numbers)'),
})

export const queryAnalyticsSchema = z.object({
  category: z
    .enum([
      'conversations',
      'messages',
      'calls',
      'contacts',
      'performance',
      'conversions',
      'credit_usage',
    ])
    .describe('Which metric category to summarize'),
  from: z.string().describe('Start date YYYY-MM-DD'),
  to: z.string().describe('End date YYYY-MM-DD'),
  interval: z.enum(['day', 'week', 'month', 'year']).default('day').describe('Bucket granularity'),
  filter: z
    .object({
      tags: z.array(z.string()).optional().describe('Tag IDs as strings, from list_tags'),
      inboxes: z
        .array(z.string())
        .optional()
        .describe('Inbox/team IDs as strings, from list_inboxes'),
      members: z.array(z.string()).optional().describe('Member IDs as strings'),
    })
    .optional(),
})
